import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { CategoryBadge } from "@/components/CategoryBadge";
import { evDate } from "@/lib/eventDate";

const EXAMPLES = ["betrayal scenes", "who could have forged the blade", "places tied to exile", "unresolved prophecies"];

function ResultCard({ r, i, onOpen }) {
  return (
    <motion.button initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} onClick={() => onOpen(r)}
      data-testid={`meaning-result-${r.id}`}
      className="group w-full text-left grid grid-cols-[48px_1fr_auto] items-start gap-4 rounded-md border border-[#2D3442] bg-[#13171F] px-5 py-4 transition-[border-color] hover:border-[#D97706]/50">
      <span className="font-mono-lf text-xs text-[#F59E0B] pt-1">{Math.round(r.score)}%</span>
      <span>
        <span className="flex items-center gap-2">
          {r.type === "entry" ? <CategoryBadge category={r.category} /> : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-[#8B5CF6]"><Clock className="h-3 w-3" />Event · {r.timeline_name}</span>}
        </span>
        <span className="block font-display text-2xl mt-1.5 text-[#F3EBDD] group-hover:text-[#F59E0B] transition-colors">{r.type === "entry" ? r.name : r.title}</span>
        {r.type === "event" && <span className="block text-xs text-[#A8A090]">{evDate(r)}</span>}
        <span className="block mt-1.5 text-sm italic text-[#E8DFD1]" data-testid={`meaning-reason-${r.id}`}>{r.reason}</span>
      </span>
      <ArrowRight className="h-4 w-4 text-[#A8A090] mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
    </motion.button>
  );
}

export const MeaningSearch = ({ uid, base }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (q = query) => {
    if (q.trim().length < 2) return;
    setQuery(q);
    setBusy(true);
    setResults(null);
    try {
      const { data } = await api.post(`/universes/${uid}/ai/search`, { query: q.trim() });
      setResults(data.results);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const open = (r) => navigate(r.type === "entry" ? `${base}/lore/${r.id}` : `${base}/timelines?timeline=${r.timeline_id}&t=${r.timestamp_int}`);

  return (
    <div className="mb-10" data-testid="meaning-search">
      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex gap-2 max-w-2xl">
        <div className="relative flex-1">
          <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#F59E0B]" />
          <Input className="pl-9 h-11 font-display text-lg" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ask by meaning: “find the betrayal scenes”" data-testid="meaning-search-input" />
        </div>
        <Button type="submit" className="h-11" disabled={busy || query.trim().length < 2} data-testid="meaning-search-button">{busy ? "Searching…" : "Search"}</Button>
      </form>
      {!results && !busy && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {EXAMPLES.map((ex) => (
            <button key={ex} onClick={() => run(ex)} data-testid={`meaning-example-${ex.split(" ")[0]}`}
              className="text-xs rounded-sm px-2.5 py-1 border border-dashed border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD] hover:border-[#D97706]/60 transition-colors">{ex}</button>
          ))}
        </div>
      )}
      {busy && <p className="mt-6 font-display text-xl text-[#E8DFD1] lf-caret" data-testid="meaning-search-loading">The librarian is reading the archive</p>}
      {results && (
        <div className="mt-6 space-y-2" data-testid="meaning-results">
          <div className="lf-label">{results.length ? `${results.length} matches for “${query}”` : `Nothing in the archive matches “${query}”.`}</div>
          {results.map((r, i) => <ResultCard key={r.id} r={r} i={i} onOpen={open} />)}
        </div>
      )}
    </div>
  );
};
